import { useQuery } from '@apollo/client';
import { NotificationsOutlined } from '@mui/icons-material';
import { Badge, IconButton } from '@mui/material';
import { isAfter, subHours } from 'date-fns';
import { isNil } from 'ramda';

import { useOpenClose } from '../../../../hooks';
import { BUSINESS_LOG_QUERY } from '../../BusinessLogQuery.gql';
import { useMaskedBiddingBusinessLog } from '../../hooks/useMaskedBiddingBusinessLog';
import { BusinessLogMaskedBiddingTooltip } from '../BusinessLogMaskedBiddingTooltip/BusinessLogMaskedBiddingTooltip';
import { BusinessLogPopover } from '../BusinessLogPopover/BusinessLogPopover';
import { BusinessLogQueryQueryVariables, TlscpLogEntry } from '@/generated/gql/graphql';

type NotificationButtonProps = Pick<BusinessLogQueryQueryVariables, 'entityId' | 'entityVariant'>;

const hasRecentEntry = (latestEntry?: TlscpLogEntry) => {
  if (isNil(latestEntry)) {
    return false;
  }

  const latestEntryDate = new Date(latestEntry.createdAt);
  const fortyEightHoursAgo = subHours(new Date(), 48);

  return isAfter(latestEntryDate, fortyEightHoursAgo);
};

export const BusinessLogPopoverButton = ({ entityId, entityVariant }: NotificationButtonProps) => {
  const [dialogProps, setDialogOpen] = useOpenClose();

  const { isMaskedBidding, isMaskedNegotiationCaseLog } = useMaskedBiddingBusinessLog({ entityId, entityVariant });

  const { data } = useQuery(BUSINESS_LOG_QUERY, {
    variables: {
      entityId,
      entityVariant,
      limit: 1,
      offset: 0,
      includeChildren: false,
    },
    skip: isMaskedNegotiationCaseLog,
  });

  const invisible = !hasRecentEntry(data?.telescope?.logEntries?.nodes?.[0]);

  return (
    <BusinessLogMaskedBiddingTooltip needsTooltip={isMaskedNegotiationCaseLog}>
      <IconButton aria-label="notifications" onClick={setDialogOpen} disabled={isMaskedNegotiationCaseLog}>
        <Badge color="secondary" variant="dot" invisible={invisible}>
          <NotificationsOutlined />
        </Badge>
      </IconButton>

      <BusinessLogPopover
        isMaskedBidding={isMaskedBidding}
        entityId={entityId}
        entityVariant={entityVariant}
        {...dialogProps}
      />
    </BusinessLogMaskedBiddingTooltip>
  );
};
